import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, Area, AreaChart,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../lib/api';
import {
  Users, TrendingUp, TrendingDown, HeartPulse, CalendarDays, PieChart as PieIcon,
  BarChart3, RefreshCw, UserPlus, UserMinus, Briefcase, Building2, Sparkles, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const PALETTE = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#3b82f6', '#a855f7', '#10b981', '#ec4899'];
const ALL = '__all__';

const ChartTip = ({ active, payload, label, unit = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-xs">
      {label != null && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <span className="font-medium">{p.value}{unit}</span></p>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, tone = 'default', testid }) => {
  const tones = {
    default: 'text-primary bg-primary/10',
    green: 'text-emerald-600 bg-emerald-500/10',
    red: 'text-red-600 bg-red-500/10',
    amber: 'text-amber-600 bg-amber-500/10',
    blue: 'text-blue-600 bg-blue-500/10',
  };
  return (
    <Card className="border-border/60" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground leading-none">{label}</p>
          <p className="text-2xl font-bold font-heading leading-tight mt-1">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ icon: Icon, title, description, children, testid, className = '' }) => (
  <Card className={`border-border/60 ${className}`} data-testid={testid}>
    <CardHeader className="pb-2">
      <CardTitle className="font-heading text-base flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /> {title}</CardTitle>
      {description && <CardDescription className="text-xs">{description}</CardDescription>}
    </CardHeader>
    <CardContent className="px-1 sm:px-4">{children}</CardContent>
  </Card>
);

const EmptyHint = ({ children }) => (
  <div className="h-full min-h-[200px] flex items-center justify-center text-xs text-muted-foreground text-center px-6">{children}</div>
);

export default function PeopleAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ locations: [], departments: [] });
  const [loc, setLoc] = useState(ALL);
  const [dep, setDep] = useState(ALL);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(today);

  useEffect(() => { analyticsAPI.getFilters().then((r) => setFilters(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (loc !== ALL) params.location_id = loc;
      if (dep !== ALL) params.department = dep;
      const { data } = await analyticsAPI.getOverview(params);
      setData(data);
    } catch (e) { toast.error('Analyse konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [from, to, loc, dep]);

  useEffect(() => { load(); }, [load]);

  const openDeptEmployees = (name) => {
    if (!name || name === 'Ohne Abteilung') return;
    navigate(`/employees?departments=${encodeURIComponent(name)}`);
  };

  const openLocationEmployees = (item) => {
    if (!item?.id) return;
    navigate(`/employees?org_unit=ou-loc-${item.id}`);
  };

  const hc = data?.headcount;
  const empTypePie = useMemo(() => (hc?.by_worktime || []).map((x) => ({ name: x.name, value: x.count })), [hc]);
  const genderPie = useMemo(() => (data?.structure?.gender || []).map((x) => ({ name: x.name, value: x.count })), [data]);
  const contractPie = useMemo(() => hc ? [{ name: 'Unbefristet', value: hc.permanent }, { name: 'Befristet', value: hc.temporary }] : [], [hc]);

  return (
    <DashboardLayout>
    <div className="space-y-6 p-1" data-testid="people-analytics-page">
      {/* Header + Filter */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl flex items-center gap-2"><BarChart3 className="h-7 w-7 text-primary" /> Personalanalyse</h1>
          <p className="text-sm text-muted-foreground mt-1">Alle Personalkennzahlen auf einen Blick – Headcount, Fluktuation, Abwesenheit & Struktur.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1"><Label className="text-[11px]">Von</Label><Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[140px]" data-testid="af-from" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Bis</Label><Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="h-9 w-[140px]" data-testid="af-to" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Standort</Label>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="af-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alle Standorte</SelectItem>
                {filters.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-[11px]">Abteilung</Label>
            <Select value={dep} onValueChange={setDep}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="af-department"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Alle Abteilungen</SelectItem>
                {filters.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="h-9" onClick={load} data-testid="af-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : data && (
        <>
          {/* KPI Karten */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Users} label="Aktive Mitarbeiter" value={hc.active} sub={`${hc.total} gesamt · ${hc.inactive} inaktiv`} testid="kpi-headcount" />
            <KpiCard icon={hc && data.fluctuation.turnover_rate > 15 ? TrendingDown : TrendingUp} tone={data.fluctuation.turnover_rate > 15 ? 'red' : 'green'} label="Fluktuationsrate" value={`${data.fluctuation.turnover_rate}%`} sub={`${data.fluctuation.exits} Austritte im Zeitraum`} testid="kpi-turnover" />
            <KpiCard icon={HeartPulse} tone="amber" label="Krankenquote" value={`${data.absence.sick_rate}%`} sub={`${data.absence.sick_days} Fehltage`} testid="kpi-sickrate" />
            <KpiCard icon={CalendarDays} tone="blue" label="Urlaubstage genommen" value={data.absence.vacation_days_taken} sub="im Zeitraum (genehmigt)" testid="kpi-vacation" />
            <KpiCard icon={UserPlus} tone="green" label="Eintritte" value={data.fluctuation.entries} sub="im Zeitraum" testid="kpi-entries" />
            <KpiCard icon={UserMinus} tone="red" label="Austritte" value={data.fluctuation.exits} sub="im Zeitraum" testid="kpi-exits" />
            <KpiCard icon={Briefcase} label="Befristet / Unbefristet" value={`${hc.temporary} / ${hc.permanent}`} sub="aktive Verträge" testid="kpi-contracts" />
            <KpiCard icon={Clock} tone="green" label="Vollzeit / Teilzeit" value={`${hc.vollzeit} / ${hc.teilzeit}`} sub={`ab ${hc.fulltime_threshold} Std./Woche = Vollzeit`} testid="kpi-worktime" />
            <KpiCard icon={Sparkles} tone="blue" label="Ø Time-to-Hire" value={data.recruiting.avg_time_to_hire_days != null ? `${data.recruiting.avg_time_to_hire_days} T` : '—'} sub={`${data.recruiting.open_jobs} offene Stellen`} testid="kpi-tth" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Headcount-Entwicklung */}
            <ChartCard icon={TrendingUp} title="Headcount-Entwicklung" description="Aktive Mitarbeiter + Ein-/Austritte (letzte 12 Monate)" testid="chart-growth" className="lg:col-span-2">
              <div className="h-[280px] w-full">
                <ResponsiveContainer>
                  <ComposedChart data={data.growth_trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                    <Tooltip content={<ChartTip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="entries" name="Eintritte" fill="#0d9488" radius={[3, 3, 0, 0]} barSize={14} />
                    <Bar dataKey="exits" name="Austritte" fill="#ef4444" radius={[3, 3, 0, 0]} barSize={14} />
                    <Line type="monotone" dataKey="active" name="Aktive MA" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {/* Belegschaft nach Standort */}
            <ChartCard icon={Building2} title="Belegschaft nach Standort" description="Balken anklicken → gefilterte Mitarbeiterliste" testid="chart-location">
              {hc.by_location.length ? (
                <div className="h-[260px] w-full" style={{ cursor: 'pointer' }}>
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={hc.by_location} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" name="Mitarbeiter" radius={[0, 4, 4, 0]} barSize={18}
                        cursor="pointer" onClick={(d) => openLocationEmployees(d?.payload || d)} data-testid="location-bar">
                        {hc.by_location.map((item, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} cursor="pointer" onClick={() => openLocationEmployees(item)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyHint>Keine Standortdaten</EmptyHint>}
            </ChartCard>

            {/* Belegschaft nach Abteilung */}
            <ChartCard icon={Users} title="Belegschaft nach Abteilung" description="Balken anklicken → gefilterte Mitarbeiterliste" testid="chart-department">
              {hc.by_department.length ? (
                <div className="h-[260px] w-full" style={{ cursor: 'pointer' }}>
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={hc.by_department} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" name="Mitarbeiter" radius={[0, 4, 4, 0]} barSize={18} fill="#6366f1"
                        cursor="pointer" onClick={(d) => openDeptEmployees(d?.name || d?.payload?.name)} data-testid="dept-bar" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyHint>Keine Abteilungsdaten hinterlegt</EmptyHint>}
            </ChartCard>

            {/* Krankheits-Trend */}
            <ChartCard icon={HeartPulse} title="Fehltage-Trend (Krankheit)" description="letzte 12 Monate" testid="chart-sick">
              <div className="h-[240px] w-full">
                <ResponsiveContainer>
                  <AreaChart data={data.absence.trend} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                    <defs><linearGradient id="sickG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                    <Tooltip content={<ChartTip unit=" Tage" />} />
                    <Area type="monotone" dataKey="sick_days" name="Fehltage" stroke="#f59e0b" strokeWidth={2} fill="url(#sickG)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {/* Altersstruktur */}
            <ChartCard icon={BarChart3} title="Altersstruktur" testid="chart-age">
              {data.structure.age_buckets.some((b) => b.count) ? (
                <div className="h-[240px] w-full">
                  <ResponsiveContainer>
                    <BarChart data={data.structure.age_buckets} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" name="Mitarbeiter" radius={[4, 4, 0, 0]} barSize={30} fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyHint>Keine Geburtsdaten hinterlegt</EmptyHint>}
            </ChartCard>

            {/* Betriebszugehörigkeit */}
            <ChartCard icon={CalendarDays} title="Betriebszugehörigkeit" testid="chart-tenure">
              {data.structure.tenure_buckets.some((b) => b.count) ? (
                <div className="h-[240px] w-full">
                  <ResponsiveContainer>
                    <BarChart data={data.structure.tenure_buckets} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" name="Mitarbeiter" radius={[4, 4, 0, 0]} barSize={30} fill="#0d9488" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyHint>Keine Eintrittsdaten hinterlegt</EmptyHint>}
            </ChartCard>

            {/* Geschlecht */}
            <ChartCard icon={PieIcon} title="Geschlechterverteilung" testid="chart-gender">
              <DonutChart data={genderPie} />
            </ChartCard>

            {/* Vertragsart */}
            <ChartCard icon={Briefcase} title="Befristet vs. Unbefristet" testid="chart-contract">
              <DonutChart data={contractPie} colors={['#0d9488', '#f59e0b']} />
            </ChartCard>

            {/* Beschäftigungsart */}
            <ChartCard icon={Clock} title="Vollzeit / Teilzeit" description={`ab ${hc.fulltime_threshold} Wochenstunden = Vollzeit`} testid="chart-emptype">
              <DonutChart data={empTypePie} colors={['#0d9488', '#f59e0b', '#94a3b8']} />
            </ChartCard>

            {/* Recruiting-Herkunft */}
            <ChartCard icon={Sparkles} title="Bewerber-Herkunftskanäle" description={`${data.recruiting.total} Bewerbungen · ${data.recruiting.hired} eingestellt`} testid="chart-recruiting">
              {data.recruiting.by_source.length ? (
                <div className="h-[240px] w-full">
                  <ResponsiveContainer>
                    <BarChart layout="vertical" data={data.recruiting.by_source} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="source" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="count" name="Bewerbungen" radius={[0, 4, 4, 0]} barSize={18}>
                        {data.recruiting.by_source.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyHint>Noch keine Bewerbungen erfasst</EmptyHint>}
            </ChartCard>
          </div>
        </>
      )}
    </div>
    </DashboardLayout>
  );
}

const DonutChart = ({ data, colors = PALETTE }) => {
  const total = (data || []).reduce((s, d) => s + (d.value || 0), 0);
  if (!total) return <EmptyHint>Keine Daten</EmptyHint>;
  return (
    <div className="h-[240px] w-full">
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip content={<ChartTip />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
